import { Box, Chip, Container, Divider, Grid, Pagination, Skeleton, Stack, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { serviceApi } from "../API/Api";
import FreelancerItem from "../Homepage Components/Freelancer/FreelancerItem";
import FreelancerItemNew from "../Homepage Components/Freelancer/FreelancerItemNew";
import Job from "../Homepage Components/Latest Job/Job";
import ServiceCardItem from "../Homepage Components/Trending Services/ServiceCardItem";
import { tokens } from "../../store/tokens";

// One config per search type instead of four near-identical fetch blocks.
// `extract` normalizes each endpoint's response shape into a plain array —
// this is also what prevents a stray object (e.g. an embedded review
// summary) from ever being treated as "the array of results" if an
// endpoint's payload shape changes.
const SEARCH_CONFIG = {
  service: {
    url: (text, page) =>
      `https://aps.songjukti.com/profile/login/api_search/sear_service.php?keyword=${encodeURIComponent(text)}&page=${page}`,
    extract: (result) => result?.services,
    getKey: (data, i) => data.pro_pic ?? data.refer ?? i,
    gridProps: { xs: 6, sm: 4, md: 3 },
    Item: ServiceCardItem,
    itemProp: "data",
  },
  freelancer: {
    url: (text, page) =>
      `https://aps.songjukti.com/profile/login/api_freelancer/new_24/expert_all_search.php?s_query=${encodeURIComponent(text)}&page=${page}`,
    extract: (result) => (Array.isArray(result) ? result : result?.data),
    getKey: (data, i) => data.refer ?? data.pro_pic ?? i,
    gridProps: { xs: 6, sm: 4, md: 3 },
    Item: FreelancerItemNew,
    itemProp: "freelancerData",
  },
  job: {
    url: (text, page) =>
      `https://aps.songjukti.com/profile/login/api_search/sear_job.php?keyword=${encodeURIComponent(text)}&page=${page}`,
    extract: (result) => (Array.isArray(result) ? result : result?.data),
    getKey: (data, i) => data.job_refer ?? i,
    gridProps: { xs: 12 },
    Item: Job,
    itemProp: "jobData",
  },
  agency: {
    url: (text, page) =>
      `https://aps.songjukti.com/profile/login/api_freelancer/new_24/agency_all_search.php?s_query=${encodeURIComponent(text)}&page=${page}`,
    extract: (result) => result?.data,
    getKey: (data, i) => data.c_refer ?? data.pro_pic ?? i,
    gridProps: { xs: 6, sm: 4, md: 3 },
    Item: FreelancerItem,
    itemProp: "freelancerData",
  },
};

const TYPE_LABEL = {
  service: "services",
  freelancer: "freelancers",
  job: "jobs",
  agency: "agencies",
};

const SearchPage = () => {
  const { text, type } = useParams();
  const config = SEARCH_CONFIG[type];

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [searchedData, setSearchedData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(null);

  // New search (different text or type) always starts back at page 1.
  useEffect(() => {
    setPage(1);
  }, [text, type]);

  useEffect(() => {
    if (!config) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setNotFound(false);

    fetch(config.url(text, page))
      .then((res) => res.json())
      .then((result) => {
        if (cancelled) return;

        const items = config.extract(result);
        const list = Array.isArray(items) ? items : [];

        setSearchedData(list);
        setNotFound(list.length === 0);

        if (result?.total_pages) {
          setTotalPages(result.total_pages);
        } else if (result?.total && result?.per_page) {
          setTotalPages(Math.ceil(result.total / result.per_page));
        } else {
          setTotalPages(1);
        }
        setTotalResults(typeof result?.total === "number" ? result.total : null);
      })
      .catch((error) => {
        console.log("SearchApiError=> ", error);
        if (!cancelled) {
          // NOTE: the original code never called setLoading(false) here, so
          // a failed request left the page stuck on the loading state
          // forever. Also resetting results/pagination so stale data from a
          // previous successful search can't linger next to an error.
          setSearchedData([]);
          setTotalPages(1);
          setTotalResults(null);
          setNotFound(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page, text, type, config]);

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Box sx={{ bgcolor: tokens.bg.page, minHeight: "100vh" }}>
      <Container sx={{ minHeight: "50vh", pb: 6 }}>
        <Stack spacing={0.5} sx={{ pt: 4, pb: 2 }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: tokens.font.heading,
              fontWeight: tokens.type.h3.fontWeight,
              fontSize: tokens.type.h3.fontSize,
              color: tokens.textOn.light.primary,
            }}
          >
            Search results
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography sx={{ fontFamily: tokens.font.body, color: tokens.textOn.light.secondary }}>
              {loading
                ? `Searching for "${text}"…`
                : totalResults !== null
                ? `${totalResults} result${totalResults === 1 ? "" : "s"} for "${text}"`
                : `${searchedData.length} result${searchedData.length === 1 ? "" : "s"} for "${text}"`}
            </Typography>
            {config && (
              <Chip
                size="small"
                label={TYPE_LABEL[type] ?? type}
                sx={{
                  fontFamily: tokens.font.body,
                  fontWeight: 600,
                  bgcolor: tokens.color.tealLight,
                  color: tokens.color.teal,
                }}
              />
            )}
          </Stack>
        </Stack>

        <Divider sx={{ mb: 3, borderColor: tokens.color.line }} />

        {!config ? (
          <Typography sx={{ fontFamily: tokens.font.body, color: tokens.textOn.light.secondary, py: 4 }}>
            Unknown search category "{type}".
          </Typography>
        ) : loading ? (
          <Grid container spacing={2}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Grid item {...config.gridProps} key={`skeleton-${i}`}>
                <Skeleton
                  variant="rounded"
                  height={config.gridProps.xs === 12 ? 120 : 220}
                  sx={{ borderRadius: `${tokens.radius.md}px` }}
                />
              </Grid>
            ))}
          </Grid>
        ) : notFound ? (
          <Stack
            justifyContent="center"
            alignItems="center"
            spacing={0.5}
            sx={{ height: "50vh" }}
          >
            <Typography
              sx={{
                fontFamily: tokens.font.heading,
                fontWeight: 700,
                color: tokens.textOn.light.primary,
              }}
            >
              No results found
            </Typography>
            <Typography sx={{ fontFamily: tokens.font.body, color: tokens.textOn.light.secondary }}>
              We couldn't find any {TYPE_LABEL[type] ?? "items"} matching "{text}".
            </Typography>
          </Stack>
        ) : (
          <>
            <Grid container spacing={2}>
              {searchedData.map((data, index) => {
                const { Item, itemProp, gridProps, getKey } = config;
                return (
                  <Grid item {...gridProps} key={getKey(data, index)}>
                    <Item {...{ [itemProp]: data }} />
                  </Grid>
                );
              })}
            </Grid>

            {totalPages > 1 && (
              <Stack alignItems="center" sx={{ mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  variant="outlined"
                  shape="rounded"
                  color="primary"
                  sx={{ mb: 4 }}
                />
              </Stack>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default SearchPage;